import { useState } from 'react';
import TemplatePreview from '../../../components/templates/TemplatePreview';
import {
  Badge,
  Box,
  ClickAwayListener,
  Collapse,
  IconButton,
  Popover,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ContactsIcon from '@mui/icons-material/Contacts';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import AddIcon from '@mui/icons-material/Add';
import { API_DELETE, API_POST, DBRequest, useConfig } from '../../../api/api';

export const TemplateGroup = ({
  group,
  onSelect,
  label,
  handleSetData,
  handleSetDeletedData,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const formats = Object.keys(group);
  const config = useConfig();

  const [extend, setExtend] = useState(false);
  const [format, setFormat] = useState(formats[0]);
  const [anchor, setAnchor] = useState(null);

  const templateFormatMap = {
    A4: t('PDF'),
    Mail: t('E-Mail'),
    Web: t('Web'),
    Attachment: t('Attachment'),
  };

  const variantIcon = {
    company: { Icon: ApartmentIcon, color: colors.info },
    contact: { Icon: ContactsIcon, color: colors.success },
  };

  function handleRightClick(e, template) {
    e.preventDefault();
    setAnchor({
      anchor: { top: e.clientY, left: e.clientX },
      template: template,
    });
  }

  function handleCopy(template) {
    setAnchor(null);

    const data = { ...template, id: null, globalId: null, vars: null };

    DBRequest({
      config,
      path: 'templates',
      method: API_POST(data),
      onResponse: (res) => handleSetData(res.data),
    });
  }

  function handleDelete(template) {
    setAnchor(null);

    DBRequest({
      config,
      path: `templates/${template.id}`,
      method: API_DELETE(),
      onResponse: () => handleSetDeletedData(template.id),
    });
  }

  return (
    <Box className="flex flex-col">
      <Box
        className="flex justify-between items-center px-3 cursor-pointer"
        onClick={() => setExtend(!extend)}
      >
        <Typography fontSize={16} fontWeight={600} className="pb-2 opacity-50">
          {`${label} (${group[format]?.length || 0})`}
        </Typography>
        <AddIcon sx={{ transform: `rotate(${extend ? '45deg' : '0deg'})` }} />
      </Box>
      {extend && (
        <Collapse in={extend}>
          <ToggleButtonGroup
            size="small"
            value={format}
            exclusive
            onChange={(e) => setFormat(e.target.value)}
            aria-label="Template Group"
          >
            {formats.map((key) => (
              <ToggleButton value={key}>{templateFormatMap[key]}</ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Box className="flex flex-wrap gap-x-4 gap-y-6 py-2">
            {group[format]?.map((template) => (
              <Box className="relative z-0">
                {template.globalId !== null && (
                  <Box
                    sx={{ bgcolor: colors.warning }}
                    className="absolute top-1 left-1 z-20 rounded-md px-2 py-0.5"
                  >
                    <Typography fontWeight={600}>{t('Global')}</Typography>
                  </Box>
                )}
                {template.isHidden && (
                  <Box className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                    <VisibilityOffIcon sx={{ fontSize: '80px' }} />
                    <Typography textAlign="center" fontWeight={500}>
                      {t('Hidden')}
                    </Typography>
                  </Box>
                )}
                <Box
                  onClick={() => onSelect(template.id)}
                  onContextMenu={(e) => handleRightClick(e, template)}
                  className="w-[300px] h-[423px] rounded-lg overflow-hidden flex z-0"
                  sx={{
                    border: `1px solid ${
                      variantIcon[template.variant]?.color || colors.selected
                    }`,
                    opacity: template.isHidden ? 0.3 : 1,
                  }}
                >
                  <TemplatePreview
                    data={template}
                    fit={true}
                    background={true}
                    paginate={false}
                    edit={true}
                    blank
                  />
                  <Typography
                    className="z-10 absolute translate-y-1/3 bottom-0 rounded-lg px-3 py-1 left-4 right-4 "
                    textAlign="center"
                    sx={{
                      bgcolor:
                        variantIcon[template.variant]?.color || colors.selected,
                    }}
                  >
                    {template.name}
                  </Typography>
                  <VariantIcon variant={variantIcon[template.variant]} />
                </Box>
                {template.mailTemplateId && (
                  <IconButton
                    sx={{ position: 'absolute' }}
                    className="top-2 right-2 z-10"
                    onClick={() => onSelect(template.mailTemplateId)}
                  >
                    <ForwardToInboxIcon />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
      <Popover
        open={Boolean(anchor)}
        anchorReference="anchorPosition"
        anchorPosition={anchor?.anchor}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        /*transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}*/
        sx={{
          '.MuiBackdrop-root': {
            backdropFilter: 'unset',
            bgcolor: 'unset',
          },
        }}
      >
        <ClickAwayListener onClickAway={() => setAnchor(null)}>
          <Box sx={{ p: 1 }}>
            <Box
              onClick={() => handleCopy(anchor.template)}
              sx={{ cursor: 'pointer', p: 1 }}
            >
              {t('Duplicate')}
            </Box>
            <Box
              className="text-red-500"
              onClick={() => handleDelete(anchor.template)}
              sx={{ cursor: 'pointer', p: 1 }}
            >
              {t('Delete')}
            </Box>
          </Box>
        </ClickAwayListener>
      </Popover>
    </Box>
  );
};

const VariantIcon = ({ variant }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  if (!variant) return;
  const { Icon, color } = variant;

  return (
    <Box className="absolute top-0 right-0 w-full h-full flex items-center justify-center z-10">
      <Icon sx={{ color: color + '50', fontSize: '90px' }} />
    </Box>
  );
};
